import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { twJoin } from "tailwind-merge";

import type { Profile } from "@/api/bale.zod";

import { useGetProfile } from "@/api/bale";
import { Missing } from "@/components/Missing";
import { SubtleButton } from "@/components/SubtleButton";

import { useCurrentEventSuspense } from "../../../hooks/useCurrentEvent";
import { EventParticipant, type EventParticipantStyle } from "./EventParticipant";
import { ProfileListModal } from "./ProfileListModal";

export function AttendeesSection({ style }: { style: EventParticipantStyle }) {
  const { t } = useTranslation();

  const seeAllAttendingModal = useRef<HTMLDialogElement>(null);

  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  function putMeFirst<T>(selector: (value: T) => Profile) {
    return (a: T, b: T) => {
      if (!me.isSuccess) return 0;
      if (selector(a).id === me.data.id) return -1;
      if (selector(b).id === me.data.id) return 1;
      return 0;
    };
  }

  const limitAttending = style === "avatar-only" ? 99 : 14;
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const host = event.data.event_participants.find(
    (participant) => participant.profile.id === event.data.event.account_id,
  )!;
  const participants = event.data.event_participants
    .filter((participant) => participant.profile.id !== event.data.event.account_id)
    .filter((participant) => participant.response === "Attending")
    .toSorted(putMeFirst((val) => val.profile));

  return (
    <div className="flex flex-col gap-4">
      <div className={twJoin("gap-2", style === "avatar-only" ? "flex flex-row flex-wrap" : "grid grid-cols-2")}>
        <div className="col-span-2">
          <EventParticipant profile={host.profile} style={style} />
        </div>
        {participants.slice(0, limitAttending).map((participant) => (
          <EventParticipant profile={participant.profile} key={participant.profile.id} style={style} />
        ))}
        {participants.length === 0 && <Missing className="col-span-2 basis-full">{t("event-attending-empty")}</Missing>}
      </div>
      {participants.length > limitAttending && (
        <>
          <SubtleButton type="button" onClick={() => seeAllAttendingModal.current?.showModal()}>
            {t("see-all")}
          </SubtleButton>
          <ProfileListModal
            title={t("event-attending-title", {
              current: participants.length + 1,
              max: event.data.event.max_attendees,
            })}
            ref={seeAllAttendingModal}
            profiles={event.data.event_participants.map((participant) => participant.profile)}
            onClose={() => seeAllAttendingModal.current?.close()}
          />
        </>
      )}
    </div>
  );
}
